/**
 * The one place Microsoft delegated scopes are written down.
 *
 * Scope strings used to live in three places at once — the env schema's
 * defaults, `.env.example`, and `docker-compose.yml` — and had already drifted:
 * compose's default carried the two GDAP scopes only, so a deployment that did
 * not set `GDAP_GRAPH_SCOPES` explicitly came up with the Copilot permissions
 * silently missing and no error until a user asked a question. Every deployment
 * path now reads its default from here, and the env schema treats an EMPTY
 * variable as "use the canonical default" so compose can pass `${VAR:-}`
 * without re-stating a list that must not fork.
 *
 * Scopes are grouped by the FEATURE that needs them, not by the resource they
 * happen to share, because that is the axis along which they are enabled:
 * GDAP's administrative permissions must not be requested for a deployment that
 * only wants the Copilot tab, and vice versa.
 */

/** The resources YazamcoOps mints delegated tokens for. */
export const MICROSOFT_GRAPH_RESOURCE = "https://graph.microsoft.com";
export const WORK_IQ_RESOURCE = "api://workiq.svc.cloud.microsoft";

/** Required for any refresh-token flow; requested once, never per feature. */
export const OFFLINE_ACCESS_SCOPE = "offline_access";

/**
 * GDAP automation. `Group.Read.All` is the least-privileged delegated
 * permission that supports the only group query the workflow makes
 * (`GET /groups?$filter=displayName`); `Directory.Read.All` would be broader
 * than the feature needs.
 */
export const GDAP_GRAPH_SCOPES = [
  `${MICROSOFT_GRAPH_RESOURCE}/DelegatedAdminRelationship.ReadWrite.All`,
  `${MICROSOFT_GRAPH_RESOURCE}/Group.Read.All`,
] as const;

/**
 * Grounding permissions for the `/beta/copilot/conversations` gateway — the
 * Graph adapter, which is now selectable AT RUNTIME rather than only by
 * `COPILOT_API`. Work IQ does its own grounding in the signed-in user's
 * context and needs none of these; they are nonetheless requested at login
 * whenever the Copilot tab is on, because the operator may switch to the Graph
 * adapter mid-session and a consent that was never asked for cannot be granted
 * without a new sign-in. They are kept OUT of `GDAP_GRAPH_SCOPES` all the
 * same: which permissions a feature needs is a property of the feature, not of
 * the resource it happens to share.
 */
export const COPILOT_GRAPH_SCOPES = [
  `${MICROSOFT_GRAPH_RESOURCE}/Sites.Read.All`,
  `${MICROSOFT_GRAPH_RESOURCE}/Mail.Read`,
  `${MICROSOFT_GRAPH_RESOURCE}/People.Read.All`,
  `${MICROSOFT_GRAPH_RESOURCE}/OnlineMeetingTranscript.Read.All`,
  `${MICROSOFT_GRAPH_RESOURCE}/Chat.Read`,
  `${MICROSOFT_GRAPH_RESOURCE}/ChannelMessage.Read.All`,
  `${MICROSOFT_GRAPH_RESOURCE}/ExternalItem.Read.All`,
] as const;

/**
 * The Microsoft 365 Copilot Retrieval API — `POST /v1.0/copilot/retrieval`.
 *
 * A SEPARATE list from `COPILOT_GRAPH_SCOPES`, not an addition to it, because
 * the two are needed by different features and are enabled independently: the
 * legacy Graph CHAT adapter needs its seven grounding permissions, and
 * retrieval needs these three, and a deployment running Work IQ chat with
 * enterprise retrieval should be asked for these and not those. Bundling them
 * would make every deployment consent to whichever list is longer.
 *
 * `Files.Read.All` is the one that is easy to get wrong, and this repository
 * had it wrong: Microsoft's reference lists `Files.Read.All` AND
 * `Sites.Read.All` as jointly least-privileged, and requires BOTH for
 * SharePoint *and* for OneDrive retrieval. `Sites.Read.All` alone — which is
 * what the Copilot chat list already carried — is not sufficient for the
 * Retrieval API and fails at the far end rather than at consent.
 *
 * `ExternalItem.Read.All` is Microsoft's higher-privileged tier here and buys
 * exactly one thing: Copilot connector (`externalItem`) retrieval. It is listed
 * separately so a tenant with no connectors is not asked for it.
 */
export const COPILOT_RETRIEVAL_GRAPH_SCOPES = [
  `${MICROSOFT_GRAPH_RESOURCE}/Files.Read.All`,
  `${MICROSOFT_GRAPH_RESOURCE}/Sites.Read.All`,
] as const;

/**
 * Attaching a file from the operator's own computer.
 *
 * A local file is not something either Copilot API can be handed: both take
 * `contextualResources.files[].uri` — a link to something that already lives in
 * OneDrive or SharePoint. So "attach from this computer" is necessarily an
 * upload FIRST, into the signed-in user's own OneDrive, and then the ordinary
 * URI attachment. This is the permission for that upload and nothing else.
 *
 * `Files.ReadWrite.AppFolder` rather than `Files.ReadWrite`, deliberately: it
 * grants write access to ONE folder that Entra creates for this application
 * inside the user's OneDrive, and no read or write access to the rest of their
 * drive. `Files.ReadWrite` would let YazamcoOps rewrite any document the person
 * owns, which is a great deal of authority to acquire for the sake of putting a
 * PDF somewhere Copilot can reach it.
 *
 * If a tenant turns out not to ground on the app folder, the fix is to widen
 * this list — not to change any calling code, which addresses the destination
 * as `special/approot` and never as a path.
 */
export const COPILOT_UPLOAD_GRAPH_SCOPES = [
  `${MICROSOFT_GRAPH_RESOURCE}/Files.ReadWrite.AppFolder`,
] as const;

/** Additionally required to retrieve from Copilot connectors. */
export const COPILOT_RETRIEVAL_CONNECTOR_SCOPES = [
  `${MICROSOFT_GRAPH_RESOURCE}/ExternalItem.Read.All`,
] as const;

/**
 * Work IQ. A SEPARATE resource from Microsoft Graph: a Graph access token is
 * not accepted by `workiq.svc.cloud.microsoft`, and Entra will not issue one
 * token covering both. The scope is therefore never mixed into a Graph token
 * request — see `modules/gdap/gdap-token.service.ts` in the API for how the
 * two audiences are kept apart.
 */
export const COPILOT_WORKIQ_SCOPES = [
  `${WORK_IQ_RESOURCE}/WorkIQAgent.Ask`,
] as const;

/** Work IQ's production REST base. Overridable for a sovereign cloud. */
export const WORK_IQ_BASE_URL_DEFAULT = "https://workiq.svc.cloud.microsoft/rest";

const join = (scopes: readonly string[]): string => scopes.join(" ");

/** Env-variable defaults, derived from the lists above rather than retyped. */
export const GDAP_GRAPH_SCOPES_DEFAULT = join([OFFLINE_ACCESS_SCOPE, ...GDAP_GRAPH_SCOPES]);
export const COPILOT_GRAPH_SCOPES_DEFAULT = join(COPILOT_GRAPH_SCOPES);
export const COPILOT_RETRIEVAL_GRAPH_SCOPES_DEFAULT = join([
  ...COPILOT_RETRIEVAL_GRAPH_SCOPES,
  ...COPILOT_RETRIEVAL_CONNECTOR_SCOPES,
]);
export const COPILOT_WORKIQ_SCOPES_DEFAULT = join(COPILOT_WORKIQ_SCOPES);
export const COPILOT_UPLOAD_GRAPH_SCOPES_DEFAULT = join(COPILOT_UPLOAD_GRAPH_SCOPES);

/** Split a space- or comma-separated scope string into deduplicated scopes. */
export function parseScopeList(value: string): string[] {
  const seen = new Set<string>();
  for (const scope of value.split(/[\s,]+/)) {
    const trimmed = scope.trim();
    if (trimmed) seen.add(trimmed);
  }
  return [...seen];
}


/**
 * The permission name inside a scope string.
 *
 * Entra is inconsistent about what it echoes in a token response's `scope`:
 * Graph permissions usually come back bare (`Mail.Read`), sometimes fully
 * qualified (`https://graph.microsoft.com/Mail.Read`), and casing is not
 * guaranteed. Comparing the trailing segment case-insensitively is the only
 * form that holds for both, and a false "missing scope" would push a working
 * user into a pointless reconnect.
 */
function scopeName(scope: string): string {
  const trimmed = scope.trim();
  const slash = trimmed.lastIndexOf("/");
  return (slash === -1 ? trimmed : trimmed.slice(slash + 1)).toLowerCase();
}

/**
 * Which of `required` is absent from `granted`.
 *
 * Empty when nothing is missing — and empty, deliberately, when Entra told us
 * nothing at all about the grant: an unknown scope set is not evidence of a
 * missing permission, and guessing would break a working chat.
 */
export function missingScopes(
  granted: readonly string[],
  required: readonly string[],
): string[] {
  if (granted.length === 0) return [];
  const have = new Set(granted.map(scopeName));
  return required.filter((scope) => !have.has(scopeName(scope)));
}

/**
 * The scope string appended to the OIDC login.
 *
 * Only Graph-family scopes belong here. Work IQ's permission is for another
 * resource and cannot be requested in the same authorization request; it is
 * obtained afterwards by redeeming the resulting refresh token against the
 * Work IQ audience, which needs delegated `WorkIQAgent.Ask` admin-consented on
 * the app registration (see docs/M365_COPILOT.md). `offline_access` is added
 * exactly once whenever anything is enabled — without it there is no refresh
 * token and nothing to redeem for either resource.
 */
export function buildLoginScopes(...scopeGroups: string[]): string {
  const scopes = scopeGroups
    .flatMap((group) => parseScopeList(group))
    .filter((scope) => !scope.startsWith(WORK_IQ_RESOURCE) && scope !== OFFLINE_ACCESS_SCOPE);
  if (scopes.length === 0) return "";
  return [OFFLINE_ACCESS_SCOPE, ...new Set(scopes)].join(" ");
}
